'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DemoSandboxProps {
  productId: string;
  productName: string;
  demoFeatures: {
    id: string;
    name: string;
    description: string;
    icon: string;
  }[];
}

export default function DemoSandbox({ productId, productName, demoFeatures }: DemoSandboxProps) {
  const [activeFeature, setActiveFeature] = useState<string>(demoFeatures[0]?.id || '');
  const [isLoading, setIsLoading] = useState(false);

  const handleFeatureChange = (featureId: string) => {
    setIsLoading(true);
    setActiveFeature(featureId);
    setTimeout(() => setIsLoading(false), 800);
  };

  const currentFeature = demoFeatures.find(f => f.id === activeFeature);

  return (
    <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
      <div className="bg-primary text-primary-foreground p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Icon name="CommandLineIcon" size={24} variant="solid" />
          <h4 className="font-display font-bold text-lg">{productName} Demo Sandbox</h4>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-success animate-pulse"></div>
          <span className="font-body text-sm">Live Demo</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-0">
        {/* Feature Selector */}
        <div className="lg:col-span-1 bg-surface p-4 border-r border-border">
          <h5 className="font-display font-semibold text-sm text-foreground mb-3">
            Demo Features
          </h5>
          <div className="space-y-2">
            {demoFeatures.map((feature) => (
              <button
                key={feature.id}
                onClick={() => handleFeatureChange(feature.id)}
                className={`w-full flex items-center space-x-3 p-3 rounded-md transition-all duration-300 ${
                  activeFeature === feature.id
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'bg-card text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={feature.icon as any} size={20} variant="outline" />
                <span className="font-body text-sm text-left">{feature.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Demo Display */}
        <div className="lg:col-span-3 p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Icon name={currentFeature?.icon as any} size={24} variant="solid" className="text-accent mt-1" />
                <div>
                  <h6 className="font-display font-bold text-lg text-foreground mb-2">
                    {currentFeature?.name}
                  </h6>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">
                    {currentFeature?.description}
                  </p>
                </div>
              </div>

              {/* Mock Demo Interface */}
              <div className="bg-surface rounded-lg p-6 border border-border mt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-card rounded-md">
                    <span className="font-body text-sm text-foreground">Sample Data Processing</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                        <div className="w-3/4 h-full bg-success animate-pulse"></div>
                      </div>
                      <span className="font-body text-xs text-muted-foreground">75%</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-card rounded-md text-center">
                      <Icon name="UserGroupIcon" size={24} variant="outline" className="text-accent mx-auto mb-2" />
                      <p className="font-body text-xs text-muted-foreground">Active Users</p>
                      <p className="font-display font-bold text-xl text-foreground">1,247</p>
                    </div>
                    <div className="p-4 bg-card rounded-md text-center">
                      <Icon name="ChartBarIcon" size={24} variant="outline" className="text-success mx-auto mb-2" />
                      <p className="font-body text-xs text-muted-foreground">Efficiency</p>
                      <p className="font-display font-bold text-xl text-foreground">94%</p>
                    </div>
                    <div className="p-4 bg-card rounded-md text-center">
                      <Icon name="ClockIcon" size={24} variant="outline" className="text-warning mx-auto mb-2" />
                      <p className="font-body text-xs text-muted-foreground">Avg Response</p>
                      <p className="font-display font-bold text-xl text-foreground">2.3s</p>
                    </div>
                  </div>

                  <div className="p-4 bg-card rounded-md">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-body font-semibold text-sm text-foreground">Recent Activity</span>
                      <Icon name="ArrowPathIcon" size={16} variant="outline" className="text-accent" />
                    </div>
                    <div className="space-y-2">
                      {[1, 2, 3].map((item) => (
                        <div key={item} className="flex items-center space-x-3 p-2 hover:bg-surface rounded transition-colors duration-200">
                          <div className="w-2 h-2 rounded-full bg-success"></div>
                          <span className="font-body text-xs text-muted-foreground flex-1">
                            Demo action completed successfully
                          </span>
                          <span className="font-body text-xs text-muted-foreground">Just now</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button className="px-4 py-2 bg-surface text-foreground rounded-md font-cta text-sm hover:bg-muted transition-all duration-300">
                  Reset Demo
                </button>
                <button className="px-4 py-2 bg-conversion text-conversion-foreground rounded-md font-cta text-sm hover:shadow-cta transition-all duration-300">
                  Request Full Access
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}